import { Button } from '@/components/ui';
import { cn } from '@/lib/utils/cn';
import { useSurveyStore } from '@/stores/survey-store';
import { useNavigationStore } from '@/stores/navigation-store';
import { useUIStore } from '@/stores/ui-store';

interface PageNavigatorProps {
  onNext: () => void;
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  className?: string;
}

export function PageNavigator({
  onNext,
  onBack,
  onSubmit,
  isSubmitting = false,
  className,
}: PageNavigatorProps) {
  const { settings } = useSurveyStore();
  const { canGoBack, isLastPage } =
    useNavigationStore();
  const { hasValidationErrors } = useUIStore();

  const showBackButton = settings.allowBackButton && canGoBack;
  const hasErrors = hasValidationErrors();

  return (
    <div className={cn('flex items-center justify-between gap-4', className)}>
      {/* Back button */}
      <div className="flex-1">
        {showBackButton && (
          <Button
            variant="outline"
            onClick={onBack}
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          >
            <svg
              className="mr-2 h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back
          </Button>
        )}
      </div>

      {/* Next/Submit button */}
      <div className="flex-1 flex justify-end">
        {isLastPage ? (
          <Button
            variant="primary"
            onClick={onSubmit}
            isLoading={isSubmitting}
            disabled={hasErrors}
            className="w-full sm:w-auto"
          >
            Submit
            <svg
              className="ml-2 h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={onNext}
            disabled={isSubmitting || hasErrors}
            className="w-full sm:w-auto"
          >
            Next
            <svg
              className="ml-2 h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </Button>
        )}
      </div>
    </div>
  );
}
